import { TextButton } from '@creditornot/cb-button';
import { Plus } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import React, { CSSProperties, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography } from 'common/typography';
import { localize } from 'common/utils';

import { MenuItemName } from './MenuItemName';
import { Breakpoints } from '../../../constants/breakpoints';
import { DroppableImageSlot } from '../../ImageSlot/DroppableImageSlot';
import { SortableImageSlot } from '../../ImageSlot/SortableImageSlot';
import { getSortableImageSlotsRenderProps } from '../../ImagesManager/utils';
import { ListRowMenuItemProps } from '../types';

const Root = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 6px 32px;
  gap: 20px;

  @media (max-width: ${Breakpoints.MOBILE}px) {
    padding: 2px 8px;
  }
`;

const imageSize = '72px';
const ImageContainer = styled.div`
  width: ${imageSize};
  height: ${imageSize};
  flex-shrink: 0;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  justify-content: center;
  min-height: ${imageSize};
  overflow: hidden;
`;

const Description = styled(Typography.Body2)`
  font-weight: 400;
  color: ${cssThemedValue('textSecondary')};
  line-clamp: 2;
  -webkit-line-clamp: 2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
`;

const AdditionalImages = styled.div`
  position: relative;
  max-width: 100%;
  display: flex;
  gap: 8px;
  padding-block-start: 4px;
`;

const SmallImageContainer = styled.div`
  width: 56px;
  height: 56px;
`;

export const RowMenuItemHeights = {
  NOT_COLLAPSED_WITH_DESCRIPTION: 104,
  NOT_COLLAPSED_WITHOUT_DESCRIPTION: 88,
  COLLAPSED_WITH_DESCRIPTION: 144,
  COLLAPSED_WITHOUT_DESCRIPTION: 98,
};

export const getRowMenuItemHeight = (item: ListRowMenuItemProps) => {
  if (item.isCollapsed && Boolean(item.description)) {
    return RowMenuItemHeights.COLLAPSED_WITH_DESCRIPTION;
  }

  if (!item.isCollapsed && Boolean(item.description)) {
    return RowMenuItemHeights.NOT_COLLAPSED_WITH_DESCRIPTION;
  }

  if (item.isCollapsed && !Boolean(item.description)) {
    return RowMenuItemHeights.COLLAPSED_WITHOUT_DESCRIPTION;
  }

  return RowMenuItemHeights.NOT_COLLAPSED_WITHOUT_DESCRIPTION;
};

export const ListRowMenuItem = ({
  style,
  id,
  name,
  description,
  images,
  isLoading,
  isReadOnly,
  isCollapsed,
  onCollapse,
  onUnlink,
}: ListRowMenuItemProps & {
  style: CSSProperties;
}) => {
  const intl = useIntl();
  const itemName = localize(name, intl.locale);
  const itemDescription = localize(description || [], intl.locale);

  const sortableImageSlots = useMemo(
    () =>
      getSortableImageSlotsRenderProps({
        menuItemId: id,
        images,
        isLoading: Boolean(isLoading),
        isReadOnly: Boolean(isReadOnly),
      }),
    [id, images, isLoading, isReadOnly],
  );

  return (
    <Root style={style}>
      <ImageContainer>
        <DroppableImageSlot
          id={`${id}-0`}
          data={{ id, imageSlotIndex: 0, images, image: images[0] }}
          image={images[0]}
          isLoading={isLoading}
          isReadOnly={isReadOnly}
          onUnlink={onUnlink}
        />
      </ImageContainer>

      <TextContainer>
        <MenuItemName>{itemName}</MenuItemName>
        {itemDescription && <Description>{itemDescription}</Description>}

        {isCollapsed ? (
          <AdditionalImages>
            <SortableContext items={sortableImageSlots} strategy={horizontalListSortingStrategy}>
              {sortableImageSlots.map(slot => {
                const ImageSlot = slot.data.image ? SortableImageSlot : DroppableImageSlot;
                return (
                  <SmallImageContainer key={slot.id}>
                    <ImageSlot
                      id={slot.id}
                      data={slot.data}
                      image={slot.data.image}
                      onUnlink={onUnlink}
                      iconSize={24}
                    />
                  </SmallImageContainer>
                );
              })}
            </SortableContext>
          </AdditionalImages>
        ) : (
          <TextButton icon={<Plus />} onClick={onCollapse} disabled={isReadOnly}>
            <FormattedMessage id="photo-shoot.photo-link.add-more-button" />
          </TextButton>
        )}
      </TextContainer>
    </Root>
  );
};
